import React from 'react';
import { errorMessage, getProjectPricing } from '../../api';
import { useAsyncData } from '../../hooks/useAsyncData';
import { formatNumber } from '../../lib/format';
import type { Project, ProjectPricing } from '../../types';
import { Button, Card, CardBody, InlineAlert, Table, Td, Th } from '../../ui';

/**
 * Estimated cost per target group and in total (GET /project/{id}/pricing): rate x wanted completes, with the rate
 * taken from the rate card for the group's incidence rate and length of interview.
 * The backend has not confirmed the rate's unit, so this shows `basis.rateUnit` (and `basis.currency` only when the
 * server sets it) and never a currency symbol of its own. A missing figure is a dash with the server's reason, not 0.
 */
export function PricingCard({ project }: { project: Project }) {
  const pricing = useAsyncData<ProjectPricing | null>((signal) => getProjectPricing(project.id, { signal }), [project.id]);
  const p = pricing.data;
  const lines = (p && p.targetGroups) || [];
  const unit = p && p.basis && p.basis.rateUnit ? String(p.basis.rateUnit) : null;
  const currency = p && p.basis && p.basis.currency ? String(p.basis.currency) : null;
  const money = (v: number | null | undefined) => (typeof v === 'number' && Number.isFinite(v) ? `${v.toLocaleString(undefined, { maximumFractionDigits: 2 })}${currency ? ` ${currency}` : ''}` : '—');

  return (
    <Card className="overflow-hidden">
      <CardBody>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-base font-semibold text-ink">Estimated cost</h2>
            <p className="mt-1 text-sm text-muted">Rate × wanted completes, using the rate card for each target group's incidence rate and interview length.</p>
          </div>
          <Button variant="ghost" size="sm" onClick={pricing.reload} loading={pricing.loading}>Refresh</Button>
        </div>
        {pricing.error ? (
          <div className="mt-4"><InlineAlert tone="warn">Could not load the pricing: {errorMessage(pricing.error)}</InlineAlert></div>
        ) : !p && !pricing.loading ? (
          <div className="mt-4"><InlineAlert tone="info">Pricing is not available for this campaign yet.</InlineAlert></div>
        ) : null}
      </CardBody>

      {p && (
        lines.length === 0 ? (
          <div className="px-5 pb-5"><InlineAlert tone="info">Add a target group to see an estimate.</InlineAlert></div>
        ) : (
          <>
            <Table caption="Estimated cost per target group">
              <thead><tr><Th>Target group</Th><Th>IR</Th><Th>LOI</Th><Th>Wanted</Th><Th>Rate{unit ? ` (${unit})` : ''}</Th><Th>Estimated cost</Th></tr></thead>
              <tbody>
                {lines.map((l, i) => (
                  <tr key={l.targetGroupId} className="hover:bg-canvas">
                    <Td className="font-semibold">{l.name || `Target group ${i + 1}`}</Td>
                    <Td className="tabular-nums text-muted">{l.incidenceRate === null || l.incidenceRate === undefined ? '—' : `${l.incidenceRate}%`}</Td>
                    <Td className="tabular-nums text-muted">{l.lengthOfInterview === null || l.lengthOfInterview === undefined ? '—' : `${l.lengthOfInterview} min`}</Td>
                    <Td className="tabular-nums text-muted">{formatNumber(l.wantedCompletes)}</Td>
                    <Td className="tabular-nums text-muted">{formatNumber(l.rate)}</Td>
                    <Td className="tabular-nums">
                      {money(l.estimatedCost)}
                      {l.notEnoughData && l.notEnoughData.length > 0 && <span className="mt-0.5 block text-xs font-normal text-muted">Not enough data: {l.notEnoughData.join('; ')}</span>}
                    </Td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t border-line bg-canvas">
                  <Td className="font-bold">Total</Td><Td /><Td />
                  <Td className="font-bold tabular-nums">{formatNumber(p.totals && p.totals.wantedCompletes)}</Td>
                  <Td />
                  <Td className="font-bold tabular-nums">{money(p.totals && p.totals.estimatedCost)}</Td>
                </tr>
              </tfoot>
            </Table>
            <p className="px-5 py-3 text-xs text-muted">
              {p.basis && p.basis.formula ? `${p.basis.formula}. ` : ''}
              {unit ? `Rate unit: ${unit}. ` : 'The rate unit has not been confirmed. '}
              {currency ? `Currency: ${currency}.` : 'No currency is set, so these are rate-card figures, not a quote.'}
            </p>
          </>
        )
      )}
    </Card>
  );
}
